// Module Import
//---------------------------------
import React, { forwardRef, useState } from 'react'
import styled from 'styled-components'
import DatePicker from 'react-datepicker'
import { InputField } from '../../atoms/fields/field'
import TypographyAtom from '../../atoms/typography/typography'
import 'react-datepicker/dist/react-datepicker.css'
import { getDay } from 'date-fns'
import { secondaryFont } from '../../../assets/consts/fonts'
import { brandIvory, brandOrange } from '../../../assets/consts/color'

// Component Import
//---------------------------------
const Form = styled.form`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  margin-bottom: 24px;
`

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  span,
  label {
    font-family: ${secondaryFont};
    font-size: 18px;
    font-weight: 400;
    letter-spacing: 0px;
    line-height: 120%;
  }
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  & > div {
    flex: 1;
  }

  @media only screen and (min-width: 48em) and (max-width: 1450px) {
    //flex-direction: column !important;
  }

  @media only screen and (min-width: 48em) {
    flex-direction: row;
  }
`

const RentalRequirementsListItem = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;

  @media only screen and (min-width: 48em) {
    margin: 0;
  }
`

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  position: relative;

  & > div:first-child:not(.input-wrapper) {
    width: 100%;
    display: flex;
    align-items: end;
  }

  .input-wrapper {
    width: 100%;
  }
`

const CopyWrapper = styled.div`
  background-color: ${brandIvory};
  border-bottom: 2px solid c00000012;
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 16px;

  h4 {
    margin: 0 0 8px 0;
    font-weight: bold;
  }

  p {
    margin: 0;

    &:first-of-type {
      color: ${brandOrange};
    }
  }
`

function parseDate(dateString) {
  const [year, month, day] = dateString.split('-')

  const dateObject = new Date(year, month - 1, day)

  return dateObject
}

function generateExcludeDates(bookings) {
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  // Getting all the dates from the beginning of the month
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
  const pastDates = []
  for (let d = new Date(currentMonthStart); d < today; d.setDate(d.getDate() + 1)) {
    pastDates.push(new Date(d))
  }

  // filtering booking dates
  const bookingDates = bookings
    .filter((booking) => !booking.available_slot_count)
    .map((booking) => parseDate(booking.date))

  const excludeDates = [...bookingDates, today, tomorrow, ...pastDates]

  return excludeDates
}

// Styled Component Declarations
//---------------------------------

const CustomInput = forwardRef((props, ref) => (
  <div className="input-wrapper">
    <label>{props?.label}</label>
    <InputField ref={ref} {...props} placeholder={props?.placeholderText} />
  </div>
))

const exclude = ['Sunday', 'Saturday']

const DAYS_OF_THE_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

// Component Export
//---------------------------------
export default function RentalForm({
  onSubmit,
  bookings,
  formValues,
  onChange,
  maxWeeks,
  price,
  fetchNextBookings,
  deliveryPrice,
}) {
  const handleExcludedDates = React.useCallback(
    (date) => {
      if (!exclude?.length) return true
      const day = getDay(date)
      return !exclude?.includes(DAYS_OF_THE_WEEK[day])
    },
    [exclude]
  )

  const date = new Date()
  const { startDate, finishDate, weeksCount } = formValues
  const basePrice = price * weeksCount

  return (
    <Form id="rentalForm" onSubmit={onSubmit}>
      <BodyWrapper>
        <FieldWrapper>
          <FieldItem>
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={startDate}
              onChange={(date) => {
                const newValues = {
                  startDate: date,
                }

                const finishDate = new Date(date).setDate(date.getDate() + weeksCount * 7)

                newValues.finishDate = finishDate

                onChange(newValues)
              }}
              customInput={<CustomInput placeholderText="dd/mm/yyyy" label="Receive Date:" />}
              enableTabLoop={false}
              filterDate={handleExcludedDates}
              excludeDates={generateExcludeDates(bookings)}
              minDate={new Date(`${date.getFullYear()}/${date.getMonth() + 1}/01`)}
              maxDate={new Date(`${date.getFullYear() + 1}/${date.getMonth() + 1}/01`)}
            />
            <RentalRequirementsListItem>
              <CopyWrapper>
                <TypographyAtom type="P" copy="Base Price" weight="Bold" />
                <TypographyAtom
                  type="P"
                  copy={`£${Number.isNaN(basePrice) ? 0 : basePrice.toFixed(2)}`}
                  weight="Regular"
                />
              </CopyWrapper>
            </RentalRequirementsListItem>
          </FieldItem>
          <FieldItem>
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={finishDate}
              enableTabLoop={false}
              customInput={
                <CustomInput placeholderText="dd/mm/yyyy" disabled label="Return Date:" />
              }
              disabled
            />
            <RentalRequirementsListItem>
              <CopyWrapper>
                <TypographyAtom type="P" copy="Delivery & Collection Price" weight="Bold" />
                <TypographyAtom
                  type="P"
                  copy={`${Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  }).format(parseFloat(deliveryPrice))}`}
                  weight="Regular"
                />
              </CopyWrapper>
            </RentalRequirementsListItem>
          </FieldItem>
          <FieldItem>
            <CustomInput
              label="No. of Weeks"
              placeholderText="Rental Duration"
              type="number"
              min={1}
              max={50}
              value={weeksCount}
              disabled={!startDate}
              onChange={(e) => {
                if (!startDate) return
                onChange({
                  weeksCount: e.target.value,
                  finishDate: new Date(startDate).setDate(startDate.getDate() + e.target.value * 7),
                })
              }}
            />
            <RentalRequirementsListItem>
              <CopyWrapper>
                <TypographyAtom type="P" copy="Insurance" weight="Bold" />
                <TypographyAtom type="P" copy="Hired-in equipment" weight="Regular" />
              </CopyWrapper>
            </RentalRequirementsListItem>
          </FieldItem>
        </FieldWrapper>
      </BodyWrapper>
    </Form>
  )
}

RentalForm.propTypes = {}
